import {
  AccessTime as AccessTimeIcon,
  Alarm as AlarmIcon,
  ExpandMore as ExpandMoreIcon,
  Assignment as PlanningIcon,
  PriorityHigh as PriorityHighIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  ChipPropsColorOverrides,
  Divider,
  LinearProgress,
  Skeleton,
  Typography,
} from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import React from "react";
import { useActiveUserWorkItems } from "../../../hooks/useActiveUserWorkItems";
import {
  WorkItemList,
  WorkItemStateEnum,
} from "../../../store/services/timelineApiRTK";
import {
  getTeamColor,
  getWorkItemColor,
  getWorkItemIcon,
  getWorkItemTypeName,
  WORK_ITEM_CATEGORIES,
  WorkItemCategory,
} from "../../../utils/workItemUtils";
import ActivityLink from "../../activity-link/ActivityLink";
import { formatDate } from "../../next-action/helpers";

const PriorityActivities = () => {
  const {
    data: workItems = [],
    isFetching,
    isLoading,
  } = useActiveUserWorkItems({
    state: WorkItemStateEnum.Active,
  });

  const formatOverdueTime = (scheduledDate: string): string => {
    const date = new Date(scheduledDate);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

    if (diffInDays === 0) {
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return `${diffInMinutes} min sedan`;
      }
      return `${diffInHours} timmar sedan`;
    } else if (diffInDays === 1) {
      return "Igår";
    } else if (diffInDays < 7) {
      return `${diffInDays} dagar sedan`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} vecka${weeks > 1 ? "r" : ""} sedan`;
    } else {
      return formatDate(scheduledDate);
    }
  };

  const { overdueScheduled, overduePlanning } = React.useMemo(() => {
    const now = new Date();

    const overdueItems = workItems
      .filter((item) => {
        if (item.State !== WorkItemStateEnum.Active) return false;
        if (!item.ScheduledTo) return false;

        const scheduledDate = new Date(item.ScheduledTo);
        if (isNaN(scheduledDate.getTime())) return false;

        return scheduledDate < now;
      })
      .sort((a, b) => {
        return (
          new Date(a.ScheduledTo).getTime() - new Date(b.ScheduledTo).getTime()
        );
      });

    // Kategorisera baserat på typ
    const planningTypes = WORK_ITEM_CATEGORIES[WorkItemCategory.COMMUNICATION];
    const followUpTypes = WORK_ITEM_CATEGORIES[WorkItemCategory.FOLLOWUP];

    const planning = overdueItems.filter((item) =>
      planningTypes.includes(item.Type)
    );
    const scheduled = overdueItems.filter((item) =>
      followUpTypes.includes(item.Type)
    );

    return {
      overduePlanning: planning.slice(0, 10), // Max 10 planering
      overdueScheduled: scheduled.slice(0, 5), // Max 5 schemalagda
    };
  }, [workItems]);

  const getPriorityLevel = (
    scheduledDate: string
  ): "critical" | "high" | "medium" => {
    const now = new Date();
    const due = new Date(scheduledDate);
    const diffInDays = Math.floor(
      (now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays > 7) return "critical";
    if (diffInDays > 3) return "high";
    return "medium";
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      default:
        return "default";
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case "critical":
        return "Kritisk";
      case "high":
        return "Hög";
      case "medium":
        return "Medium";
      default:
        return "Låg";
    }
  };

  if (isLoading) {
    return (
      <Card elevation={0} sx={{ border: "1px solid", borderColor: "grey.200" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="medium">
            Förfallna aktiviteter
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width="100%"
                height={60}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  const totalOverdue = overduePlanning.length + overdueScheduled.length;

  return (
    <Card elevation={0} sx={{ border: "1px solid", borderColor: "grey.200" }}>
      {isFetching && <LinearProgress />}
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6" fontWeight="medium">
            Förfallna aktiviteter
            {totalOverdue > 0 && (
              <Chip
                label={totalOverdue}
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {totalOverdue === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.secondary",
              }}
            >
              <PriorityHighIcon
                sx={{
                  fontSize: 48,
                  mb: 1,
                  opacity: 0.5,
                  color: "success.main",
                }}
              />
              <Typography
                variant="body2"
                color="success.main"
                fontWeight="medium"
              >
                Inga förfallna aktiviteter! 🎉
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Bra jobbat med att hålla koll på allt
              </Typography>
            </Box>
          ) : (
            <>
              {/* Overdue Scheduled Activities Section */}
              {overdueScheduled.length > 0 && (
                <OverdueScheduledSection
                  activities={overdueScheduled}
                  formatOverdueTime={formatOverdueTime}
                  getPriorityLevel={getPriorityLevel}
                  getPriorityColor={getPriorityColor}
                  getPriorityLabel={getPriorityLabel}
                />
              )}

              {/* Overdue Planning Activities Section */}
              {overduePlanning.length > 0 && (
                <OverduePlanningSection
                  activities={overduePlanning}
                  formatOverdueTime={formatOverdueTime}
                />
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PriorityActivities;

// Overdue Scheduled Activities (Critical - needs immediate attention)
interface OverdueScheduledSectionProps {
  activities: WorkItemList[];
  formatOverdueTime: (date: string) => string;
  getPriorityLevel: (date: string) => string;
  getPriorityColor: (
    priority: string
  ) => OverridableStringUnion<
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning",
    ChipPropsColorOverrides
  >;
  getPriorityLabel: (priority: string) => string;
}

const OverdueScheduledSection: React.FC<OverdueScheduledSectionProps> = ({
  activities,
  formatOverdueTime,
  getPriorityLevel,
  getPriorityColor,
  getPriorityLabel,
}) => {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <AlarmIcon sx={{ color: "error.main" }} />
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ color: "error.main" }}
        >
          Missade uppföljningar
        </Typography>
        <Chip
          label={activities.length}
          size="small"
          color="error"
          sx={{ fontWeight: "bold" }}
        />
        <Typography variant="caption" sx={{ color: "error.main", ml: 1 }}>
          Krävs omedelbar åtgärd
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {activities.map((item) => (
          <OverdueScheduledItem
            key={item.Id}
            workItem={item}
            priority={getPriorityLevel(item.ScheduledTo)}
            formatOverdueTime={formatOverdueTime}
            getPriorityColor={getPriorityColor}
            getPriorityLabel={getPriorityLabel}
          />
        ))}
      </Box>
    </Box>
  );
};

// Overdue Planning Activities (Less urgent - can be batched)
interface OverduePlanningSectionProps {
  activities: WorkItemList[];
  formatOverdueTime: (date: string) => string;
}

const OverduePlanningSection: React.FC<OverduePlanningSectionProps> = ({
  activities,
  formatOverdueTime,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <Box>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "warning.main",
          "&:before": { display: "none" },
          borderRadius: 2,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <PlanningIcon sx={{ color: "warning.dark" }} />
            <Typography
              variant="subtitle1"
              fontWeight="medium"
              sx={{ color: "warning.dark" }}
            >
              Planering som dragit över tid
            </Typography>
            <Chip
              label={activities.length}
              size="small"
              sx={{
                bgcolor: "warning.main",
                color: "white",
                fontWeight: "bold",
              }}
            />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: "warning.dark" }}>
              Schemalägg eller avsluta
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {activities.map((item) => (
              <OverduePlanningItem
                key={item.Id}
                workItem={item}
                formatOverdueTime={formatOverdueTime}
              />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// Overdue Scheduled Item (High priority styling)
interface OverdueScheduledItemProps {
  workItem: WorkItemList;
  priority: string;
  formatOverdueTime: (date: string) => string;
  getPriorityColor: (
    priority: string
  ) => OverridableStringUnion<
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning",
    ChipPropsColorOverrides
  >;
  getPriorityLabel: (priority: string) => string;
}

const OverdueScheduledItem: React.FC<OverdueScheduledItemProps> = ({
  workItem,
  priority,
  formatOverdueTime,
  getPriorityColor,
  getPriorityLabel,
}) => {
  const activityTypeColor = getWorkItemColor(workItem.Type);
  const teamColor = getTeamColor(workItem.Team?.Id);
  const priorityColor = getPriorityColor(priority);

  return (
    <ActivityLink
      to={`/activity/${workItem.Id}`}
      style={{ textDecoration: "none", color: "inherit" }}
      customerNo={workItem.Customer.Id}
      workItemNo={workItem.Id}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: "2px solid",
          borderColor: `${priorityColor}.main`,
          position: "relative",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: `${priorityColor}.dark`,
            transform: "translateY(-1px)",
            boxShadow: 2,
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -8,
            right: -8,
            width: 16,
            height: 16,
            borderRadius: "50%",
            bgcolor: `${priorityColor}.main`,
            border: "2px solid white",
            animation: priority === "critical" ? "pulse 2s infinite" : "none",
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: teamColor,
              color: "white",
              width: 40,
              height: 40,
              fontSize: "0.875rem",
              fontWeight: "bold",
              border: "2px solid white",
            }}
          >
            {workItem.Customer?.Name?.charAt(0) || "?"}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: `${priorityColor}.dark`,
              }}
            >
              {workItem.Customer?.Name || "Ingen kund"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.5,
                flexWrap: "wrap",
              }}
            >
              <Chip
                icon={getWorkItemIcon(workItem.Type)}
                label={getWorkItemTypeName(workItem.Type)}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: "0.7rem",
                  bgcolor: `${activityTypeColor}15`,
                  borderColor: activityTypeColor,
                  color: activityTypeColor,
                  "& .MuiChip-icon": {
                    color: activityTypeColor,
                  },
                }}
              />

              <Chip
                label={getPriorityLabel(priority)}
                size="small"
                color={priorityColor}
                sx={{ height: 18, fontSize: "0.7rem", fontWeight: "bold" }}
              />
            </Box>

            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
            >
              <AccessTimeIcon
                sx={{ color: `${priorityColor}.dark`, fontSize: 14 }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: `${priorityColor}.dark`,
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                }}
              >
                Förföll {formatOverdueTime(workItem.ScheduledTo)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </ActivityLink>
  );
};

// Overdue Planning Item (Lower priority styling)
interface OverduePlanningItemProps {
  workItem: WorkItemList;
  formatOverdueTime: (date: string) => string;
}

const OverduePlanningItem: React.FC<OverduePlanningItemProps> = ({
  workItem,
  formatOverdueTime,
}) => {
  const activityTypeColor = getWorkItemColor(workItem.Type);
  const teamColor = getTeamColor(workItem.Team?.Id);

  return (
    <ActivityLink
      to={`/activity/${workItem.Id}`}
      style={{ textDecoration: "none", color: "inherit" }}
      customerNo={workItem.Customer.Id}
      workItemNo={workItem.Id}
    >
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "grey.300",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: "warning.main",
            bgcolor: "warning.light",
            transform: "translateX(4px)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: teamColor,
              color: "white",
              width: 32,
              height: 32,
              fontSize: "0.75rem",
            }}
          >
            {workItem.Customer?.Name?.charAt(0) || "?"}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {workItem.Customer?.Name || "Ingen kund"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                icon={getWorkItemIcon(workItem.Type)}
                label={getWorkItemTypeName(workItem.Type)}
                size="small"
                variant="outlined"
                sx={{
                  height: 16,
                  fontSize: "0.65rem",
                  bgcolor: `${activityTypeColor}10`,
                  borderColor: activityTypeColor,
                  color: activityTypeColor,
                  "& .MuiChip-icon": {
                    color: activityTypeColor,
                  },
                }}
              />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {formatOverdueTime(workItem.ScheduledTo)} sedan
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </ActivityLink>
  );
};
